import { useEffect, useState, useCallback, FocusEvent } from 'react';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import { dawaAutocomplete } from 'dawa-autocomplete2';
import { generateUniqueId } from '../../shared/helpers/helpers';
import { useStyles } from './address-field.styles';

/*
 Relevant links, some is Danish only.
 DAWA:
 https://github.com/DanmarksAdresser/dawa-autocomplete2 or perhaps
 https://github.com/Dataforsyningen/dawa-autocomplete2
 https://dawadocs.dataforsyningen.dk/dok/api/autocomplete
 https://dawadocs.dataforsyningen.dk/dok/adresser
 https://danmarksadresser.dk/media/9909/samlet-vejledning-i-adressereglerne-010419-endelig.pdf
 BBR:
 https://dawadocs.dataforsyningen.dk/dok/bbr
 https://ki.bbr.dk/file/664783/nye-anvendelseskoder-bygninger.pdf
*/

/*
 This component tries to fail gracefully if DAWA or BBR is down. The component will be disabled until
 DAWA is verified to be up. If DAWA is down an error will be shown in the message field under the component.
 */

type FieldEventDataProps = {
    address: string;
    addressInfo: {
        zipCode: string;
        streetName: string;
        houseNumber: string;
        postalCodeName: string;
        supCityName: string;
        floor: string;
        door: string;
    };
    status: string;
    event: string;
    kvhxUrl: string;
    kvhx: string | undefined;
    inBBR: boolean | undefined;
};

type TDawaIsUp = 'none' | 'checking' | 'yes' | 'no';

// The event fired from the DawaComponent when the user select an address.
type DawaEvent = {
    caretpos: number; // Not important for us.
    forslagstekst: string; // Not important for us. Will contain \n between some address parts.
    tekst: string; // The text of the option chossen.
    type: string; // Not important for us. Will be set to "adresse".
    stormodtagerpostnr: boolean; // Not important for us. Will be set to false.
    data: {
        id: string; // Unique ID for that address, used in call to BBR.
        status: number; // Not important for us.
        darstatus: number; // Not important for us.
        vejkode: string; // Not important for us.
        vejnavn: string; // Name of street
        adresseringsvejnavn: string; // Not important for us. Short version of vejnavn.
        husnr: string; // Number on street
        etage: string | null; // Floor
        dør: string | null; // Door
        supplerendebynavn: string | null; // Supplementary city name
        postnr: string; // Postal code
        postnrnavn: string; // Postal code name
        stormodtagerpostnr: string | null; // Not important for us.
        stormodtagerpostnrnavn: string | null; // Not important for us.
        kommunekode: string; // Not important for us.
        adgangsadresseid: string; // Not important for us.
        x: number; // Not important for us. GPS coordinate.
        y: number; // Not important for us. GPS coordinate.
        href: string; // URL to get more details about the address.
    };
};

interface IFieldEventFunction {
    (v: FieldEventDataProps | string): void;
}

type AddressFieldProps = {
    /**
     * The ID of the input field. Used for filling the correct suggestion box if there are more components like this on a page.
     */
    id?: string;
    /**
     * The html label of the field
     */
    label?: string;
    /**
     * If true, the input element will be required.
     */
    required?: boolean;
    /**
     * If true, the input element will be disabled.
     */
    disabled?: boolean;
    /**
     * Show information that shows the user what he did wrong.
     */
    errorText?: string;
    /**
     * Triggers the error state of the field
     */
    error?: boolean;
    /**
     * The autocomplete will start after the user types this many characters in the field.
     */
    minLength?: number;
    /**
     * The default value of the field. Use if you already have the address of the user.
     */
    defaultValue?: string;
    /**
     * Prop to signal that the field is submitted
     */
    isSubmitted?: boolean;
    /**
     * Function that will recieve the data from the field
     */
    fieldEvent: IFieldEventFunction;
    /**
     * Controls the margin above and below the field
     */
    margin?: 'normal' | 'dense' | 'none';
} & TextFieldProps;

const AddressField = ({
    id,
    label = 'Adresse',
    fieldEvent,
    required = false,
    errorText = 'Ugyldig adresse',
    disabled = false,
    minLength = 3,
    defaultValue,
    isSubmitted,
    margin = 'normal',
    error = false,
    className = '',
    ...rest
}: AddressFieldProps) => {
    const DAWACHECKURL =
        'https://api.dataforsyningen.dk/autocomplete?q=Borupvang%204&type=adresse&caretpos=11&supplerendebynavn=true&stormodtagerpostnumre=false&multilinje=true&fuzzy=';
    const DAWAMAXTIMEOUT = 8000; // 8 seconds
    const DAWAISDOWNTEXT = 'Tekniske problemer - prøv igen senere';
    let BBRCheckUrl =
        'https://api.dataforsyningen.dk/adresser/autocomplete?q={{address}}&type=adresse&stormodtagerpostnumre=false&multilinje=true';
    const classes = useStyles();
    const [elemId] = useState(id || `addressfield-${generateUniqueId()}`);
    const [dawaIsUp, setDawaIsUp] = useState<TDawaIsUp>('none');
    const [errorMsg, setErrorMsg] = useState(errorText);
    const [isError, setIsError] = useState(error);
    const [element, setElement] = useState<string[]>([]);
    const [value, setValue] = useState<DawaEvent | undefined>(undefined);
    const [kvhx, setKvhx] = useState<string | undefined>(undefined);
    const [inBBR, setInBBR] = useState<boolean | undefined>(undefined);

    useEffect(() => {
        setErrorMsg(errorText);
    }, [errorText]);

    useEffect(() => {
        setIsError(error);
    }, [error]);

    function handleOnBlur(event: FocusEvent<HTMLInputElement>): void {
        // If no value selected then the field is invalid
        // If value.tekst !== event.target.value then user has changed the field without selecting, so also invalid
        if (value && value.tekst === event.target.value) {
            setIsError(false);
            fieldEvent({
                address: value.tekst,
                addressInfo: {
                    zipCode: value.data.postnr,
                    streetName: value.data.vejnavn,
                    houseNumber: value.data.husnr,
                    postalCodeName: value.data.postnrnavn,
                    supCityName: value.data.supplerendebynavn || '',
                    floor: value.data.etage || '',
                    door: value.data.dør || '',
                },
                status: 'valid',
                event: 'blur',
                kvhx: kvhx,
                kvhxUrl: value.data.href,
                inBBR: inBBR,
            });
        } else {
            setIsError(true);
            fieldEvent({
                address: '',
                addressInfo: {
                    zipCode: '',
                    streetName: '',
                    houseNumber: '',
                    postalCodeName: '',
                    supCityName: '',
                    floor: '',
                    door: '',
                },
                status: 'invalid',
                event: 'blur',
                kvhx: kvhx,
                kvhxUrl: '',
                inBBR: inBBR,
            });
        }
    }

    function handleOnFocus() {
        setIsError(false);
    }

    const dawaSelectHandler = useCallback(
        async (event: DawaEvent) => {
            setIsError(false);

            // Always use https, never http
            event.data.href = event.data.href.replace(/^http:/, 'https:');
            const detailsPromise = fetch(event.data.href).then(
                async (response) => {
                    const resp = await response.json().then(
                        (ok) => ok,
                        () => undefined
                    );
                    return resp;
                },
                () => undefined
            );

            let bbrUrl = '';
            if (!event.data.etage) {
                const checkAddress = encodeURIComponent(
                    `${event.data.adresseringsvejnavn} ${event.data.husnr}, ¤, ${event.data.postnr} ${event.data.postnrnavn}`
                );
                bbrUrl = BBRCheckUrl;
                bbrUrl = bbrUrl.replaceAll('{{address}}', checkAddress);
            }

            const bbrPromise = fetch(bbrUrl).then(
                async (response) => {
                    const resp = await response.json().then(
                        (ok) => {
                            return ok;
                        },
                        (_d) => {
                            return undefined;
                        }
                    );

                    return resp;
                },
                (_dd) => {
                    return undefined;
                }
            );

            const [details, bbr] = await Promise.all([detailsPromise, bbrPromise]);
            let kvhx = undefined;
            if (details && details.kvhx) {
                kvhx = details.kvhx;
            }
            setKvhx(kvhx);

            const bbrFound = (event.data.etage && bbr === undefined) || bbr?.length === 1;

            setInBBR(bbrFound);

            setValue(event);
            fieldEvent({
                address: event.tekst,
                addressInfo: {
                    zipCode: event.data.postnr,
                    streetName: event.data.vejnavn,
                    houseNumber: event.data.husnr,
                    postalCodeName: event.data.postnrnavn,
                    supCityName: event.data.supplerendebynavn || '',
                    floor: event.data.etage || '',
                    door: event.data.dør || '',
                },
                status: 'valid',
                event: 'select',
                kvhx: kvhx,
                kvhxUrl: event.data.href,
                inBBR: bbrFound,
            });
        },
        [fieldEvent, event as unknown as DawaEvent]
    );

    useEffect(() => {
        if (dawaIsUp === 'none' && !isError) {
            setDawaIsUp('checking');
            let timeoutId: any;
            const fetchData = async () => {
                const abortController = new AbortController();
                timeoutId = setTimeout(() => {
                    abortController.abort();
                }, DAWAMAXTIMEOUT);

                return fetch(DAWACHECKURL, { signal: abortController.signal }).then((response) => {
                    if (response.ok) {
                        clearTimeout(timeoutId);
                        setDawaIsUp('yes');
                        return Promise.resolve(response);
                    } else {
                        clearTimeout(timeoutId);
                        return Promise.reject(response);
                    }
                });
            };

            fetchData().catch(() => {
                clearTimeout(timeoutId);
                setDawaIsUp('no');
                setIsError(true);
                setErrorMsg(DAWAISDOWNTEXT);
                fieldEvent('DAWA not working');
            });
        }
    }, [dawaIsUp, isError]);

    useEffect(() => {
        const inputElement = document.getElementById(elemId);
        // element is a sanity check use array for multiple dawa instances
        // Otherwise it will create so many dawa instances in the DOM cause an error
        if (inputElement && !element.some((ele) => ele === elemId)) {
            setElement((prev) => [...prev, elemId]);
            dawaAutocomplete(inputElement, {
                adgangsadresserOnly: false,
                fuzzy: true,
                stormodtagerpostnumre: false,
                minLength: minLength,
                multiline: false,
                select: dawaSelectHandler,
            });
        }
    }, [elemId, minLength, dawaSelectHandler, element]);

    return (
        <TextField
            error={isError || (isSubmitted && !value?.data.href)}
            required={required}
            disabled={disabled || dawaIsUp !== 'yes'}
            label={label}
            helperText={isError ? errorMsg : ''}
            autoComplete={'off'}
            onBlur={handleOnBlur}
            onFocus={handleOnFocus}
            defaultValue={defaultValue && defaultValue}
            className={`${className} ${classes.flos_address_field}`}
            fullWidth={true}
            margin={margin}
            id={elemId}
            {...rest}
        />
    );
};

export { AddressField };
export type { AddressFieldProps };
export type { FieldEventDataProps };
export type { IFieldEventFunction };
